import React from "react";
import { Flame, BookOpen, Mic, TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const TREND_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus, new: Sparkles };
const TREND_COLOR = { up: "text-forest-700", down: "text-terracotta-600", flat: "text-muted-foreground", new: "text-gold-600" };

export default function PulseReport({ pulse, hideStudentHeader }) {
  const { t: tr } = useLanguage();
  if (!pulse) return null;
  const TrendIcon = TREND_ICON[pulse.accuracy_trend] || Minus;
  const trendColor = TREND_COLOR[pulse.accuracy_trend] || "text-muted-foreground";
  const maxDay = Math.max(1, ...pulse.daily_activity.map((d) => d.count));

  return (
    <div className="space-y-6" data-testid="pulse-report">
      {/* Header */}
      {!hideStudentHeader && (
        <div className="flex items-center gap-4">
          {pulse.student.picture ? (
            <img src={pulse.student.picture} alt="" className="h-14 w-14 rounded-full object-cover" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-forest-700 text-sand-100 grid place-items-center font-display font-semibold text-xl">
              {pulse.student.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{tr("pulseReport.hifzPulse")}</div>
            <h1 className="font-display font-semibold text-3xl text-forest-700">{tr("pulseReport.sWeek", { name: pulse.student.name })}</h1>
            <p className="text-sm text-muted-foreground">{pulse.period.week_start} — {pulse.period.week_end}</p>
          </div>
        </div>
      )}

      {/* Top tiles */}
      <div className="grid grid-cols-2 gap-4">
        <Tile testid="pulse-tile-active" icon={Flame} tint="bg-terracotta-500/12 text-terracotta-600"
              label={tr("pulseReport.activeDays")} value={`${pulse.active_days}/7`} />
        <Tile testid="pulse-tile-memorized" icon={BookOpen} tint="bg-forest-600/12 text-forest-700"
              label={tr("pulseReport.newAyahs")} value={pulse.ayahs_memorized_week} suffix="memorized" />
        <Tile testid="pulse-tile-attendance" icon={Sparkles} tint="bg-gold-500/15 text-gold-600"
              label={tr("pulseReport.halaqaAttended")} value={pulse.attendance_count ?? 0} suffix="sessions" />
        <Tile testid="pulse-tile-recitations" icon={Mic} tint="bg-forest-600/12 text-forest-700"
              label={tr("pulseReport.recitationTests")} value={pulse.recitation_count} />
      </div>

      {/* Revision & goals (AI Hifz Coach insights for parents) */}
      {pulse.coach && (
        <div className="hifz-card" data-testid="pulse-coach">
          <h2 className="font-display font-semibold text-lg text-forest-700">{tr("pulseReport.revisionGoals")}</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="font-display font-bold text-2xl text-forest-700">{pulse.coach.daily_goal_ayahs}</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-1">{tr("pulseReport.dailyGoalAyahs")}</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl text-forest-700">{pulse.coach.reviews_completed_week}</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-1">{tr("pulseReport.reviewsThisWeek")}</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl text-forest-700">{pulse.coach.avg_review_accuracy != null ? tr("pulseReport.text", { avg_review_accuracy: pulse.coach.avg_review_accuracy }) : "—"}</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-1">{tr("pulseReport.avgReviewScore")}</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl text-forest-700">{pulse.coach.revision_due_today}</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-1">{tr("pulseReport.dueToday")}</div>
            </div>
          </div>
          {pulse.coach.achievements_week?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-sand-300 flex flex-wrap items-center gap-2" data-testid="pulse-achievements">
              <span className="text-xs font-semibold text-gold-700">{tr("pulseReport.newBadges")}</span>
              {pulse.coach.achievements_week.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-full bg-gold-500/12 border border-gold-500/30 text-xs text-gold-700">{t}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity chart */}
      <div className="hifz-card" data-testid="pulse-activity">
        <div className="flex items-end justify-between">
          <h2 className="font-display font-semibold text-lg text-forest-700">{tr("pulseReport.dailyActivity")}</h2>
          <span className="text-xs text-muted-foreground">{tr("pulseReport.ayahsMarkedPerDay")}</span>
        </div>
        <div className="mt-6 flex items-end justify-between gap-2 h-44">
          {pulse.daily_activity.map((d, i) => {
            const h = d.count > 0 ? Math.max(12, (d.count / maxDay) * 160) : 6;
            const isToday = i === pulse.daily_activity.length - 1;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div className="text-xs font-display font-semibold text-foreground/70">{d.count || ""}</div>
                <div
                  className={`w-full rounded-t-lg transition-all duration-700 ${
                    d.count === 0 ? "bg-sand-300" : isToday ? "bg-gradient-to-t from-forest-600 to-gold-500" : "bg-forest-600/80"
                  }`}
                  style={{ height: `${h}px` }}
                />
                <div className="text-xs text-muted-foreground">{d.day}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Accuracy + mistake words */}
      <div className="grid gap-5">
        <div className="hifz-card" data-testid="pulse-accuracy">
          <h2 className="font-display font-semibold text-lg text-forest-700">{tr("pulseReport.recitationAccuracy")}</h2>
          {pulse.avg_accuracy === null ? (
            <p className="mt-4 text-sm text-muted-foreground">{tr("pulseReport.noRecitationTestsThisWeek")}</p>
          ) : (
            <>
              <div className="mt-5 flex items-end gap-3">
                <div className="font-display font-bold text-5xl text-forest-700">{pulse.avg_accuracy}%</div>
                <div className={`inline-flex items-center gap-1 text-sm font-medium ${trendColor}`}>
                  <TrendIcon className="h-4 w-4" />
                  {pulse.accuracy_trend === "new" && tr("pulseReport.firstWeekOfTests")}
                  {pulse.accuracy_trend === "up" && tr("pulseReport.vsLastWeek", { accuracy_trend_delta: pulse.accuracy_trend_delta })}
                  {pulse.accuracy_trend === "down" && tr("pulseReport.vsLastWeek2", { accuracy_trend_delta: pulse.accuracy_trend_delta })}
                  {pulse.accuracy_trend === "flat" && tr("pulseReport.steady")}
                </div>
              </div>
              <div className="mt-4 h-2 bg-sand-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-forest-600 to-gold-500" style={{ width: `${pulse.avg_accuracy}%` }} />
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                {tr("pulseReport.matchScore")} <span className="font-semibold text-gold-600">{pulse.avg_tajweed}%</span>
                {pulse.prev_avg_accuracy !== null && <> {tr("pulseReport.prevWeek", { prev_avg_accuracy: pulse.prev_avg_accuracy })}</>}
              </div>
            </>
          )}
        </div>

        <div className="hifz-card" data-testid="pulse-mistakes">
          <h2 className="font-display font-semibold text-lg text-forest-700">{tr("pulseReport.top3MistakeWords")}</h2>
          {pulse.top_mistakes.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">
              {pulse.recitation_count === 0
                ? tr("pulseReport.noDataYetTryA")
                : tr("pulseReport.noRepeatingMistakesMashallah")}
            </p>
          ) : (
            <ul className="mt-5 space-y-3">
              {pulse.top_mistakes.map((m, i) => (
                <li key={i} className="flex items-center justify-between gap-4 p-3 rounded-xl bg-sand-200/50 border border-sand-300">
                  <div className="font-arabic text-3xl text-forest-700" dir="rtl">{m.word}</div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{tr("pulseReport.stumbled")}</div>
                    <div className="font-display font-semibold text-lg text-terracotta-600">{m.count}×</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Surahs */}
      {pulse.surahs_touched.length > 0 && (
        <div className="hifz-card" data-testid="pulse-surahs">
          <h2 className="font-display font-semibold text-lg text-forest-700">{tr("pulseReport.surahsYouVisited")}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {pulse.surahs_touched.map((s) => (
              <span key={s.number} className="inline-flex items-center gap-2 rounded-full bg-white border border-sand-300 px-3 py-1.5 text-sm">
                <span className="font-arabic text-lg text-forest-700">{s.name_ar}</span>
                <span className="text-muted-foreground">· {s.name_en}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Mashallah message */}
      <div className="hifz-card grain bg-forest-700 text-sand-100 border-forest-700" data-testid="pulse-message">
        <div className="text-xs uppercase tracking-[0.22em] text-gold-400 font-semibold">{tr("pulseReport.aNoteForTheWeek")}</div>
        <p className="mt-3 text-lg font-display leading-relaxed">{pulse.mashallah_message}</p>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, suffix, tint, testid }) {
  return (
    <div data-testid={testid} className="hifz-card">
      <div className={`h-10 w-10 rounded-xl grid place-items-center ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-4 font-display font-bold text-3xl text-forest-700">
        {value}{suffix && <span className="text-sm text-muted-foreground ml-2 font-normal">{suffix}</span>}
      </div>
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
